import React, { useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableCell,
  SxProps,
  Tooltip,
} from '@mui/material';

import { ArrowDownward } from '@mui/icons-material';
import { SortQuery } from '../../store/modules/paylink/types';
import { SkeletonRows } from './SkeletonRows';

const ROW_HEIGHT = 57;

export type Column<T> = {
  headerName: string;
  id?: string;
  field?: keyof T extends string ? keyof T : never;
  renderCell?: (row: T) => React.ReactNode;
  valueGetter?: (row: T) => string;
  /**
   * A function that determines if a row should be rendered.
   * If not provided, the row will be rendered.
   */
  shouldRender?: () => boolean;

  minWidth?: number;
  maxWidth?: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
  /** When set, header label alignment can differ from row cell `align` (e.g. centered title over right-aligned buttons). */
  headerAlign?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sortName?: string; // The name of the field to sort by - defaults to the field prop's value
  paddingRight?: number;
};

export const getSortableFields = (columns: Column<any>[]) => {
  const sortableFields: string[] = [];
  columns.forEach(
    (column) =>
      column.sortable &&
      !!(column.sortName || column.field) &&
      sortableFields.push(column.sortName || column.field!),
  );
  return sortableFields;
};

const Wrapper = React.forwardRef<
  HTMLDivElement,
  {
    height: string;
    fitContent?: boolean;
    renderPagination?: () => React.ReactNode;
    children: React.ReactNode;
  }
>(({ height, fitContent = false, children, renderPagination }, ref) => (
  <Box sx={{ mb: 2 }}>
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: (theme) => theme.palette.grey[100],
        ...(fitContent ? {} : { height }),
        overflowX: 'auto',
      }}
      ref={ref}
    >
      {children}
    </TableContainer>
    {renderPagination?.()}
  </Box>
));

Wrapper.displayName = 'Wrapper';

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  isLoading?: boolean;
  /** Skeleton for fast loads; spinner for longer fetches (optional message under spinner). */
  loadingVariant?: 'skeleton' | 'spinner';
  loadingMessage?: string;
  tableRef: React.RefObject<HTMLDivElement>;
  pageSize?: number;
  tableHeight?: string;
  noRowsMessage?: string;
  renderPagination?: () => React.ReactNode;
  sort?: SortQuery;
  setSort?: (sort: SortQuery) => void;
  /** When true, sortable column headers do not accept clicks (e.g. while a fetch is in flight). */
  sortDisabled?: boolean;
  /** `cycle`: unsorted → asc → desc → unsorted. `toggle`: asc ↔ desc (first click on a column starts at asc). */
  sortCycle?: 'cycle' | 'toggle';
  /** When true, sizes the table to fit all rows without an internal scroll container. */
  fitContent?: boolean;
  rowSx?: (row: T) => SxProps;
}

const getColumnKey = <T,>(column: Column<T>) => column.id ?? column.field ?? column.headerName;

/**
 * @param columns - Array of columns to display with all their properties
 * @param rows - Array of rows to display
 * @param getRowId - Function to get the id of a row, used for the key of the row
 * @param isLoading - Boolean to indicate if the data is being fetched
 * @param pageSize - Number of rows to display per page, used to display skeleton rows. Default: 10
 * @param tableHeight - The height of the table. Default: 85vh
 * @param noRowsMessage - Message to display when there are no rows to display. Default: 'No data to display'
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  isLoading,
  loadingVariant = 'skeleton',
  loadingMessage = 'Loading...',
  tableRef,
  sort = {},
  setSort = () => {},
  sortDisabled = false,
  sortCycle = 'cycle',
  pageSize = 10,
  tableHeight = '85vh',
  noRowsMessage = 'No data to display',
  renderPagination = () => false,
  rowSx,
  fitContent = false,
}: Props<T>) {
  // Reset the scroll position of the table when the rows or pageSize change
  useEffect(() => {
    if (tableRef.current) {
      // eslint-disable-next-line no-param-reassign
      tableRef.current.scrollLeft = 0;
    }
  }, [rows, pageSize, tableRef]);

  // If the screen is too tall, limit the height of the table to the maxTableHeight so
  // that there is no white space at the bottom of the table
  const maxTableHeight = (pageSize + 1) * ROW_HEIGHT + 2;
  const height = fitContent ? 'auto' : `min(calc(${tableHeight} - 55px), ${maxTableHeight}px)`;

  const renderedColumns = columns.filter((column) => !column.shouldRender || column.shouldRender());

  if (rows.length === 0 && !isLoading) {
    return (
      <Wrapper
        height={height}
        fitContent={fitContent}
        ref={tableRef}
        renderPagination={renderPagination}
      >
        <Typography
          sx={{
            textAlign: 'center',
            mb: 2,
            mt: 4,
          }}
        >
          {noRowsMessage}
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      height={height}
      fitContent={fitContent}
      ref={tableRef}
      renderPagination={renderPagination}
    >
      <Table stickyHeader aria-label="sticky table" sx={{ position: 'relative' }}>
        <TableHead
          sx={{
            position: 'sticky',
            top: 0,
            background: (theme) => theme.palette.grey[100],
            padding: 2.5,
            zIndex: 1,
          }}
        >
          <TableRow sx={{ height: ROW_HEIGHT }}>
            {renderedColumns.map((column) => {
              const { width, minWidth, maxWidth, paddingRight, sortable } = column;
              const sortName = column.sortName ?? column.field;
              if (sortable && !sortName) {
                // eslint-disable-next-line no-console
                console.warn('Sortable column is missing sortName or field', column);
              }
              const isSorted = sortName === sort.sortBy;

              const headerAlign = column.headerAlign ?? column.align;
              const headerJustify =
                headerAlign === 'right'
                  ? 'flex-end'
                  : headerAlign === 'center'
                    ? 'center'
                    : 'flex-start';

              const canSort = sortable && !sortDisabled;

              const headerContent = (
                <Box
                  role={canSort ? 'button' : undefined}
                  aria-label={canSort ? `Sort ${column.headerName}` : undefined}
                  sx={{
                    p: 2,
                    fontWeight: 600,
                    fontSize: '12px',
                    color: (theme) => theme.palette.grey[600],
                    cursor: canSort ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: headerJustify,
                    gap: 0.5,
                    '&:hover': {
                      '& svg': {
                        opacity: isSorted ? 1 : 0.6,
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '12px',
                      color: (theme) => theme.palette.grey[600],
                    }}
                  >
                    {column.headerName}
                  </Typography>
                  {sortable && (
                    <ArrowDownward
                      sx={{
                        fontSize: 16,
                        color: (theme) => theme.palette.common.black,
                        opacity: isSorted ? 1 : 0.3,
                        transition: 'opacity 0.1s, transform 0.1s',
                        transform: isSorted && sort.orderBy === 'asc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  )}
                </Box>
              );

              // Determine next sort state
              const getNextSort = () => {
                if (!isSorted) {
                  return { ...(sortName && { sortBy: sortName }), orderBy: 'asc' as const };
                }
                if (sort.orderBy === 'asc') {
                  return { ...(sortName && { sortBy: sortName }), orderBy: 'desc' as const };
                }
                if (sortCycle === 'toggle') {
                  return { ...(sortName && { sortBy: sortName }), orderBy: 'asc' as const };
                }
                return {};
              };

              return (
                <TableCell
                  onClick={() => canSort && setSort(getNextSort())}
                  key={getColumnKey(column)}
                  align={headerAlign}
                  style={{
                    width,
                    minWidth,
                    maxWidth,
                    paddingRight,
                    padding: 0,
                  }}
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[100],
                  }}
                >
                  {sortable ? (
                    <Tooltip title="Click to sort" arrow enterDelay={300}>
                      {headerContent}
                    </Tooltip>
                  ) : (
                    headerContent
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && loadingVariant === 'spinner' ? (
            <TableRow>
              <TableCell colSpan={renderedColumns.length} sx={{ border: 0, py: 8 }}>
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress aria-label="Loading" />
                  <Typography variant="body2" color="text.secondary">
                    {loadingMessage}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : isLoading ? (
            <SkeletonRows pageSize={pageSize} />
          ) : (
            rows.map((row) => (
              <TableRow
                hover
                role="checkbox"
                tabIndex={-1}
                key={getRowId(row)}
                sx={{ pr: 1, ...rowSx?.(row) }}
              >
                {renderedColumns.map((column) => {
                  if (!column.field && !column.valueGetter && !column.renderCell) {
                    // eslint-disable-next-line no-console
                    console.warn('Column is missing field, valueGetter or renderCell', column);
                    return null;
                  }

                  return (
                    <TableCell
                      key={getColumnKey(column)}
                      align={column.align}
                      style={{
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        paddingRight: column.paddingRight,
                        ...(fitContent ? {} : { height: ROW_HEIGHT }),
                      }}
                    >
                      {column.renderCell ? (
                        column.renderCell(row)
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          {column.valueGetter
                            ? column.valueGetter(row)
                            : row[column.field as string]}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Wrapper>
  );
}
